import { ToastWithError, ToastWithIcon } from '../navigation/routes/toastProvider';
import { stringsRes } from '../resources/strings';
import { WebViewChannel, isDeviceIOS, isWebViewAndroid, sendMessageToNativeApp } from './webview';

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve((reader.result as string).split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export default class HtmlUtils {
    /**
     * Create a download link and show a UI to download it
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    static createDownloadAction(datas: any, headers: any): void {
        if (!datas || !headers) {
            console.error('Missing infos', { datas, headers });
            return;
        }
        const contentType = headers['content-type'];
        const contentDisposition = headers['content-disposition'];

        if (!contentDisposition) {
            console.error('no ContrentDisposition', { headers });
            return;
        }

        const filename = contentDisposition
            .split(';')
            .find((n) => n.includes('filename='))
            .replace('filename=', '')
            .replace(/"/g, '')
            .trim();

        if (isWebViewAndroid()) {
            blobToBase64(datas).then((base64) => {
                sendMessageToNativeApp(WebViewChannel.BLOB_ANDROID, { base64, filename, contentType });
            });
        } else {
            let file = datas;
            if (!(datas instanceof Blob)) {
                file = new Blob([datas], { type: contentType });
            }
            const anchor = document.createElement('a');
            anchor.download = decodeURIComponent(filename) ?? 'file';
            anchor.href = (window.webkitURL || window.URL).createObjectURL(file);
            anchor.dataset.downloadurl = [contentType, anchor.download, anchor.href].join(':');
            anchor.click();
            URL.revokeObjectURL(anchor.href);
        }
    }
}
export function preventScrollBounce(containerSelector: string): () => void {
    let lastY = 0;
    const targetElt = document.querySelector(containerSelector);

    if (!targetElt || !isDeviceIOS()) return () => {};

    const handleTouchStart = (event: TouchEvent): void => {
        lastY = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent): void => {
        const top = event.touches[0].clientY;
        const { scrollTop, scrollHeight, offsetHeight } = event.currentTarget as HTMLElement;
        const maxScrollTop = scrollHeight - offsetHeight;
        const direction = lastY - top < 0 ? 'up' : 'down';

        if (
            event.cancelable &&
            ((scrollTop <= 0 && direction === 'up') || (scrollTop >= maxScrollTop && direction === 'down'))
        ) {
            event.preventDefault();
        }

        lastY = top;
    };

    // touchmove stays non passive since it can call preventDefault
    targetElt.addEventListener('touchstart', handleTouchStart, { passive: true });
    targetElt.addEventListener('touchmove', handleTouchMove);

    // Return cleanup function
    return () => {
        targetElt.removeEventListener('touchstart', handleTouchStart);
        targetElt.removeEventListener('touchmove', handleTouchMove);
    };
}

const copyToClipboard = async (text: string): Promise<void> => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'absolute';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }
        console.log('Text copied successfully!');
        ToastWithIcon('success', stringsRes.common.copied);
    } catch (err) {
        console.error('Failed to copy text: ', err);
        ToastWithError(new Error(`${stringsRes.common.copyFailed}: ${err.message}`));
    }
};

export { copyToClipboard };
